import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, CreditCard, CheckCircle, Loader2, Calendar, Info, AlertTriangle, Settings, X, ArrowDown, ArrowUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ManageAccount = () => {
  const { token, getAuthHeaders, user, isCalendarAdmin, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPlan, setCurrentPlan] = useState('Free');
  const [currentStatus, setCurrentStatus] = useState('active');
  const [plans, setPlans] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  
  // Consent modal state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    if (!isCalendarAdmin()) {
      navigate('/dashboard');
      return;
    }
    setCurrentPlan(user?.plan_type || 'Free');
    setCurrentStatus(user?.plan_status || 'active');
    fetchPlans();
    fetchBillingInfo();
    fetchSubscriptionStatus();
  }, [isCalendarAdmin, navigate, user]);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await axios.get(`${API_BASE_URL}/plans/`);
      const activePlans = response.data
        .filter(plan => plan.is_active)
        .sort((a, b) => a.price - b.price);
      setPlans(activePlans);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load available plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      setBillingLoading(true);
      const response = await axios.get(`${API_BASE_URL}/auth/billing-info`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setBillingInfo(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching billing info:', err);
    } finally {
      setBillingLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payments/subscription-status`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setSubscriptionStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  };

  const handleUpgradeClick = (plan) => {
    // Get current plan price for comparison
    const currentPlanData = plans.find(p => p.name.toLowerCase() === currentPlan?.toLowerCase());
    const currentPrice = currentPlanData?.price || 0;
    const targetPrice = plan.price;
    
    // Debug logging
    console.log('Plan click:', {
      targetPlan: plan.name,
      targetPrice,
      currentPlan,
      currentPrice,
      hasSubscription: subscriptionStatus?.has_subscription,
      subscriptionStatus
    });
    
    // Determine if this is a downgrade
    const isDowngrade = targetPrice < currentPrice;
    const hasActiveSubscription = subscriptionStatus?.has_subscription || 
                                   (user?.stripe_subscription_id && user?.subscription_type === 'recurring');
    
    if (plan.price === 0) {
      // Downgrade to Free plan
      setSelectedPlan(plan);
      setShowDowngradeModal(true);
    } else if (isDowngrade && hasActiveSubscription) {
      // Downgrade to lower paid plan
      setSelectedPlan(plan);
      setShowDowngradeModal(true);
    } else {
      // Upgrade - show consent modal
      setSelectedPlan(plan);
      setConsentChecked(false);
      setShowConsentModal(true);
    }
  };

  const handleDowngrade = async () => {
    if (!selectedPlan) return;
    
    const planName = selectedPlan.name.toLowerCase();
    
    if (selectedPlan.price === 0) {
      // Downgrade to Free - cancel subscription
      try {
        setLoading(true);
        setError('');
        
        await axios.post(
          `${API_BASE_URL}/payments/cancel-subscription`,
          {},
          { headers: getAuthHeaders() }
        );
        
        setShowDowngradeModal(false);
        setSuccess('Your subscription will be cancelled at the end of the billing period. You\'ll retain access to current features until then.');
        await fetchSubscriptionStatus();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to cancel subscription');
      } finally {
        setLoading(false);
      }
    } else {
      // Downgrade to lower paid plan
      try {
        setLoading(true);
        setError('');
        
        const response = await axios.post(
          `${API_BASE_URL}/payments/downgrade`,
          { new_plan_id: planName },
          { headers: getAuthHeaders() }
        );
        
        if (response.data.success) {
          setShowDowngradeModal(false);
          setSuccess(response.data.message);
          // Refresh user data and subscription status
          if (refreshUser) await refreshUser();
          await fetchSubscriptionStatus();
        } else {
          setError(response.data.message || 'Failed to schedule downgrade');
        }
      } catch (err) {
        console.error('Downgrade error:', err);
        setError(err.response?.data?.detail || 'Failed to schedule downgrade');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post(
        `${API_BASE_URL}/payments/cancel-downgrade`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setSuccess(response.data.message);
        await fetchSubscriptionStatus();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel downgrade');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentAndSubscribe = async () => {
    if (!consentChecked || !selectedPlan) return;
    
    setShowConsentModal(false);
    await handlePlanChange(selectedPlan, true);
  };

  const handlePlanChange = async (plan, withConsent = false) => {
    const planName = plan.name.toLowerCase();
    
    if (planName === currentPlan?.toLowerCase()) {
      setError('You are already on this plan');
      return;
    }

    // For free plan, update directly
    if (plan.price === 0) {
      try {
        setLoading(true);
        setError('');
        setSuccess('');

        await axios.put(
          `${API_BASE_URL}/auth/me`,
          { plan_type: planName },
          { headers: getAuthHeaders() }
        );

        setCurrentPlan(planName);
        setSuccess(`Successfully switched to ${plan.name} plan!`);
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to update plan');
      } finally {
        setLoading(false);
      }
      return;
    }

    // For paid plans, use Stripe subscription checkout with consent
    try {
      setCheckoutLoading(plan.id);
      setError('');
      
      const response = await axios.post(
        `${API_BASE_URL}/payments/subscribe`,
        {
          plan_id: planName,
          origin_url: window.location.origin,
          auto_renew_consent: true,
          consent_timestamp: new Date().toISOString()
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.data.checkout_url) {
        const checkoutUrl = response.data.data.checkout_url;
        
        // Detect if running inside an iframe (e.g., preview environment)
        const isInIframe = window.self !== window.top;
        
        if (isInIframe) {
          // Open in new tab for iframe environments (Stripe doesn't work in iframes)
          window.open(checkoutUrl, '_blank');
          setSuccess('Stripe checkout opened in a new tab. Complete your payment there.');
          setCheckoutLoading(null);
        } else {
          // Direct redirect for normal browser environment
          window.location.href = checkoutUrl;
        }
      } else {
        setError('Unable to start checkout. Please try again.');
        setCheckoutLoading(null);
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleManagePaymentMethod = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/payments/manage-subscription`,
        { origin_url: window.location.origin },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.data.portal_url) {
        window.location.href = response.data.data.portal_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to open subscription management');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await axios.put(
        `${API_BASE_URL}/auth/me`,
        { plan_status: newStatus },
        { headers: getAuthHeaders() }
      );

      setCurrentStatus(newStatus);
      setSuccess(`Account status updated to ${newStatus === 'on_hold' ? 'On Hold' : 'Active'}!`);
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update account status');
    } finally {
      setLoading(false);
    }
  };

  const isCurrentPlan = (plan) => {
    return currentPlan?.toLowerCase() === plan.name.toLowerCase();
  };

  const getButtonText = (plan) => {
    if (isCurrentPlan(plan)) return 'Current Plan';
    if (plan.price === 0) return 'Downgrade';
    
    const currentPlanObj = plans.find(p => p.name.toLowerCase() === currentPlan?.toLowerCase());
    if (currentPlanObj && plan.price > currentPlanObj.price) return 'Upgrade';
    if (currentPlanObj && plan.price < currentPlanObj.price) return 'Downgrade';
    
    return 'Select Plan';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Manage Your Account</h1>
            <p className="text-gray-600 mt-2">Upgrade your plan or manage your subscription</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              {success}
            </div>
          )}

          {/* Current Plan Status */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Current Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-600">Plan Type</p>
                <p className="text-2xl font-bold text-yellow-600 capitalize">{currentPlan || 'Free'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Account Status</p>
                <p className={`text-2xl font-bold capitalize ${
                  currentStatus === 'active' ? 'text-green-600' : 
                  currentStatus === 'on_hold' ? 'text-orange-600' : 
                  'text-red-600'
                }`}>
                  {currentStatus === 'on_hold' ? 'On Hold' : currentStatus}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Billing Cycle</p>
                {billingLoading ? (
                  <p className="text-lg text-gray-400">Loading...</p>
                ) : billingInfo ? (
                  <p className="text-lg font-semibold text-gray-800">
                    {billingInfo.billing_day_display} of each month
                  </p>
                ) : (
                  <p className="text-lg text-gray-400">Not set</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600">Next Billing Date</p>
                {billingLoading ? (
                  <p className="text-lg text-gray-400">Loading...</p>
                ) : billingInfo?.is_free_plan ? (
                  <p className="text-lg text-gray-500">N/A (Free Plan)</p>
                ) : billingInfo ? (
                  <div>
                    <p className="text-lg font-semibold text-gray-800 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-yellow-500" />
                      {billingInfo.next_billing_date}
                    </p>
                    {billingInfo.plan_price > 0 && (
                      <p className="text-sm text-gray-500">${billingInfo.plan_price}/{billingInfo.billing_cycle}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-lg text-gray-400">Not available</p>
                )}
              </div>
            </div>
            
            {/* Special day note */}
            {billingInfo?.special_day_note && !billingInfo?.is_free_plan && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start">
                <Info className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">{billingInfo.special_day_note}</p>
              </div>
            )}

            {/* Subscription Management Button - shown for all Calendar Admins */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/billing')}
                data-testid="manage-billing-btn"
                className="flex items-center px-4 py-2 rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Billing & Payments
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Manage payment methods, view invoices, and update billing address
              </p>
            </div>

            {/* Pending Downgrade Banner */}
            {subscriptionStatus?.pending_downgrade && (
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <ArrowDown className="w-5 h-5 text-orange-500 mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-orange-800">Pending Plan Downgrade</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        Your plan will change from <strong className="capitalize">{currentPlan}</strong> to{' '}
                        <strong className="capitalize">{subscriptionStatus.pending_downgrade.to_plan}</strong> on{' '}
                        <strong>{new Date(subscriptionStatus.pending_downgrade.effective_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          timeZone: 'UTC'
                        })}</strong>.
                      </p>
                      <p className="text-sm text-orange-600 mt-2">
                        You'll continue to have access to all {currentPlan} features until the change takes effect.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCancelDowngrade}
                    disabled={loading}
                    className="ml-4 px-3 py-1 text-sm bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md transition-colors"
                    data-testid="cancel-downgrade-btn"
                  >
                    Cancel Downgrade
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Plans */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Choose Your Plan</h2>
            
            {plansLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                <span className="ml-3 text-gray-600">Loading plans...</span>
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No plans available at the moment.
              </div>
            ) : (
              <div className={`grid grid-cols-1 gap-6 ${
                plans.length === 2 ? 'md:grid-cols-2' :
                plans.length === 3 ? 'md:grid-cols-3' :
                plans.length >= 4 ? 'md:grid-cols-4' : ''
              }`}>
                {plans.map((plan, index) => {
                  const isPopular = index === 1 || (plans.length === 2 && index === 1);
                  const isCurrent = isCurrentPlan(plan);
                  
                  return (
                    <div
                      key={plan.id}
                      className={`bg-white rounded-lg shadow-md p-6 relative ${
                        isPopular ? 'ring-2 ring-yellow-500' : ''
                      } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
                    >
                      {isPopular && !isCurrent && (
                        <div className="absolute top-0 right-0 bg-yellow-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold">
                          Popular
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute top-0 left-0 bg-green-500 text-white px-3 py-1 rounded-br-lg rounded-tl-lg text-sm font-semibold flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Current
                        </div>
                      )}
                      <div className="mb-6 mt-4">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                        <div className="flex items-baseline">
                          <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                          <span className="text-gray-600 ml-2">/{plan.billing_cycle || 'month'}</span>
                        </div>
                        {plan.description && (
                          <p className="text-sm text-gray-500 mt-2">{plan.description}</p>
                        )}
                      </div>
                      <ul className="space-y-3 mb-6">
                        {plan.display_features?.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600 text-sm">{feature}</span>
                          </li>
                        ))}
                        {plan.display_features?.length > 6 && (
                          <li className="text-yellow-600 text-sm font-medium">
                            +{plan.display_features.length - 6} more features
                          </li>
                        )}
                      </ul>
                      <button
                        onClick={() => handleUpgradeClick(plan)}
                        disabled={loading || isCurrent || checkoutLoading === plan.id}
                        data-testid={`plan-${plan.name.toLowerCase()}-btn`}
                        className={`w-full py-3 px-4 rounded-md font-semibold transition-colors flex items-center justify-center ${
                          isCurrent
                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                            : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                        }`}
                      >
                        {checkoutLoading === plan.id ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          getButtonText(plan)
                        )}
                      </button>
                      {plan.price > 0 && !isCurrent && (
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Secure payment via Stripe
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Account Status Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <CreditCard className="w-6 h-6 mr-2" />
              Account Status Management
            </h2>
            <p className="text-gray-600 mb-6">
              You can temporarily put your account on hold or reactivate it at any time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => handleStatusChange('active')}
                disabled={loading || currentStatus === 'active'}
                className={`flex-1 py-3 px-6 rounded-md font-semibold transition-colors ${
                  currentStatus === 'active'
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {currentStatus === 'active' ? '✓ Account Active' : 'Activate Account'}
              </button>
              <button
                onClick={() => handleStatusChange('on_hold')}
                disabled={loading || currentStatus === 'on_hold'}
                className={`flex-1 py-3 px-6 rounded-md font-semibold transition-colors ${
                  currentStatus === 'on_hold'
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}
              >
                {currentStatus === 'on_hold' ? '✓ Account On Hold' : 'Put Account On Hold'}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              <strong>Note:</strong> Putting your account on hold will prevent new bookings but existing appointments will remain active.
            </p>
          </div>
        </div>
      </section>

      {/* Auto-Renewal Consent Modal */}
      {showConsentModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-800">Subscription Confirmation</h3>
              <button
                onClick={() => setShowConsentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-lg font-semibold text-gray-800">{selectedPlan.name} Plan</p>
                <p className="text-3xl font-bold text-yellow-600">
                  ${selectedPlan.price}
                  <span className="text-base text-gray-600 font-normal">/month</span>
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Auto-Renewal Information
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Your subscription will automatically renew each month</li>
                  <li>• You'll be charged ${selectedPlan.price} on your billing date</li>
                  <li>• You can cancel anytime from your account settings</li>
                  <li>• Full payment applies to the remaining billing cycle</li>
                </ul>
              </div>
              
              <label className="flex items-start space-x-3 cursor-pointer" data-testid="consent-checkbox-label">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  data-testid="auto-renewal-consent-checkbox"
                />
                <span className="text-sm text-gray-700">
                  I agree to automatic monthly billing of the amount of the Plan I subscribed to, until I cancel.
                </span>
              </label>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowConsentModal(false)}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConsentAndSubscribe}
                disabled={!consentChecked}
                data-testid="subscribe-now-btn"
                className={`flex-1 py-3 px-4 rounded-md font-semibold transition-colors ${
                  consentChecked
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Subscribe Now
              </button>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              🔒 Secured by Stripe. Your card details are never stored on our servers.
            </p>
          </div>
        </div>
      )}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center">
                <ArrowDown className="w-6 h-6 text-orange-500 mr-2" />
                <h3 className="text-xl font-bold text-gray-800">Confirm Downgrade</h3>
              </div>
              <button
                onClick={() => setShowDowngradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Error display in modal */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {error}
              </div>
            )}
            
            <div className="mb-6">
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Current Plan</p>
                  <p className="text-lg font-semibold text-gray-800 capitalize">{currentPlan}</p>
                </div>
                <ArrowDown className="w-6 h-6 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">New Plan</p>
                  <p className="text-lg font-semibold text-orange-600">{selectedPlan.name}</p>
                  <p className="text-sm text-gray-600">${selectedPlan.price}/month</p>
                </div>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-orange-800 mb-2 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Important Information
                </h4>
                <ul className="text-sm text-orange-700 space-y-2">
                  <li>• <strong>Current billing cycle:</strong> No changes - you keep all current features</li>
                  <li>• <strong>No refunds:</strong> Your subscription is pre-paid for this cycle</li>
                  <li>• <strong>Next billing cycle:</strong> You'll be charged ${selectedPlan.price}/month</li>
                  <li>• <strong>Feature changes:</strong> Take effect on your next billing date</li>
                </ul>
              </div>
              
              {billingInfo?.next_billing_date && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Effective Date:</strong> Your downgrade will take effect on{' '}
                    <strong>{billingInfo.next_billing_date}</strong>
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDowngradeModal(false)}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDowngrade}
                disabled={loading}
                data-testid="confirm-downgrade-btn"
                className="flex-1 py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ArrowDown className="w-5 h-5 mr-2" />
                    Confirm Downgrade
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageAccount;
